/**
 * The player's own ship, heard through the hull (audio-direction §3 "Thrusters and boost"): the main-drive
 * rumble loop follows forward thrust (level, pitch and brightness), the boost pressure loop runs while boosting,
 * and each RCS axis puffs when its thrust rises past a threshold or flips direction (re-arming below a lower
 * one), rate-limited per axis. Reads the flight model's actual output (`Thrust`), not the stick. Allocation-free.
 */
import { LOOP, ROUTE, type AudioEngine } from './engine'
import type { ShipSoundConfig } from './sfxSchema'
import { PRIORITY } from './voices'

/** RCS axes in `ShipThrust` order: sway, heave, pitch, yaw, roll, and surge backwards. */
const AXES = 6

/** The flight output fractions (−1..1), as in the `Thrust` component. */
export interface ShipThrust {
  surge: number
  sway: number
  heave: number
  pitch: number
  yaw: number
  roll: number
}

export interface ShipSoundState {
  readonly armed: Uint8Array
  readonly sign: Int8Array
  readonly last: Float64Array
  /** Puffs played (tests and the lab). */
  puffs: number
}

export function createShipSoundState(): ShipSoundState {
  return { armed: new Uint8Array(AXES).fill(1), sign: new Int8Array(AXES), last: new Float64Array(AXES).fill(-Infinity), puffs: 0 }
}

type ShipSink = Pick<AudioEngine, 'play' | 'setLoop'>

function axisValue(t: ShipThrust, axis: number): number {
  switch (axis) {
    case 0:
      return t.sway
    case 1:
      return t.heave
    case 2:
      return t.pitch
    case 3:
      return t.yaw
    case 4:
      return t.roll
    default:
      return t.surge < 0 ? t.surge : 0
  }
}

/** Where a puff sounds: side thrusters fire on the side opposite the push; rotations split front/back. */
function puffPan(axis: number, sign: number, cfg: ShipSoundConfig['rcs']): number {
  if (axis === 0) return -sign * cfg.pan
  if (axis === 3 || axis === 4) return sign * cfg.pan * 0.6
  return 0
}

function logLerp(a: number, b: number, u: number): number {
  return a * Math.pow(b / a, u)
}

/**
 * One frame of ship sounds. `alive` false (dead or no player) stops the loops; `rcsPuff` is the puff's sound
 * index. `now` is the audio context time.
 */
export function stepShipSounds(
  state: ShipSoundState,
  cfg: ShipSoundConfig,
  thrust: ShipThrust,
  boosting: boolean,
  alive: boolean,
  now: number,
  rcsPuff: number,
  sink: ShipSink,
): void {
  const d = cfg.drive
  const forward = alive && thrust.surge > 0 ? (thrust.surge > 1 ? 1 : thrust.surge) : 0
  const level = boosting ? d.boost : d.idle + (d.full - d.idle) * forward
  sink.setLoop(LOOP.drive, alive, level, d.rateMin + (d.rateMax - d.rateMin) * forward, logLerp(d.lowpassMin, d.lowpassMax, boosting ? 1 : forward))
  sink.setLoop(LOOP.boost, alive && boosting, cfg.boostLoop.gain, 1, 20000)
  if (!alive) return
  const r = cfg.rcs
  for (let a = 0; a < AXES; a++) {
    const v = axisValue(thrust, a)
    const mag = v < 0 ? -v : v
    const sign = v < 0 ? -1 : 1
    if (mag < r.release) {
      state.armed[a] = 1
      continue
    }
    const flipped = mag >= r.threshold && state.sign[a] !== 0 && sign !== state.sign[a]
    if (mag >= r.threshold && (state.armed[a] === 1 || flipped) && now - state.last[a] >= r.minInterval) {
      sink.play(rcsPuff, r.gain * (mag > 1 ? 1 : mag), ROUTE.hull, PRIORITY.near, puffPan(a, sign, r))
      state.last[a] = now
      state.armed[a] = 0
      state.puffs++
    }
    state.sign[a] = sign
  }
}
